import React, { useRef, useState } from 'react'
import { Pencil, ListOrdered } from 'lib/lucide'
import Box from '@material-ui/core/Box'
import Chip from '@material-ui/core/Chip'
import Tooltip from '@material-ui/core/Tooltip'
import DOMPurify from 'dompurify'
import { useTranslation } from 'react-i18next'

import { snackbar, ToggleSwitch } from 'components'
import { ERRORS } from 'consts'
import { useApi, useApiCall } from 'hooks'
import { COLORS } from 'styles'
import { StringUtils } from 'utils'

import { getParkingConfigs, setParkingConfigActive } from './api'
import {
  getMembershipChipStyle,
  getParkingKind,
  getParkingKindI18nKey,
  getParkingKindRank,
  isParkingConfigEffective,
} from './constants'
import {
  GetParkingConfigsResponse,
  ParkingConfig,
  ParkingConfigResponse,
  SetParkingConfigActivePayload,
} from './types'
import { DefaultTable } from '../components'
import {
  CreateParkingConfigDialog,
  UpdateParkingConfigDialog,
  DeleteParkingConfigDialog,
  OrderParkingBenefitsDialog,
} from './components'

const getParkingConfigsResponseGetter = (responseData: GetParkingConfigsResponse) =>
  responseData?.parkingConfigs ?? []

const getConfigPrize = (config: ParkingConfig) => config.levels?.[0]?.prize

const getConfigTitle = (config: ParkingConfig) => getConfigPrize(config)?.title ?? '-'

const getPlainConfigTitle = (config: ParkingConfig) =>
  StringUtils.extractTextFromHTML(getConfigTitle(config)) || '-'

const removeAccents = (str?: string | null) => {
  if (!str) return ''
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
}

const matchesBenefitSearch = (term: string, config: ParkingConfig) =>
  removeAccents(getPlainConfigTitle(config))
    .toLowerCase()
    .includes(removeAccents(term).toLowerCase())

const getKindLabel = (config: ParkingConfig, translate: (key: string) => string) =>
  translate(getParkingKindI18nKey(getParkingKind(config)))

const isOutOfValidity = (config: ParkingConfig): boolean =>
  Boolean(config.active && config.isFree && !isParkingConfigEffective(config))

const getModeLabel = (config: ParkingConfig, translate: (key: string) => string) => {
  if (config.isFree || config.externalTicket) return translate('parking:emptyValue')
  if (config.unlockMode) return translate(`parking:unlockModes.${config.unlockMode}`)
  return translate('parking:emptyValue')
}

const compareText = (a: string, b: string) => a.localeCompare(b, 'es', { sensitivity: 'base' })

const column95Style = { maxWidth: 95, width: 95, minWidth: 95 }
const column232Style = { maxWidth: 232, width: 232, minWidth: 232 }

const ParkingConfigs: React.FC = () => {
  const { t } = useTranslation()
  const translate = (key: string) => t(key) as string
  const [{ data: parkingConfigs, isLoading }, , setParkingConfigs] = useApi<
    GetParkingConfigsResponse,
    ParkingConfig[]
  >(getParkingConfigs, getParkingConfigsResponseGetter, {
    baseData: [],
    onError: () => snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE),
  })

  const [setActiveApi] = useApiCall<SetParkingConfigActivePayload, ParkingConfigResponse>(
    setParkingConfigActive,
  )

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isOrderDialogOpen, setIsOrderDialogOpen] = useState(false)
  const [selectedConfig, setSelectedConfig] = useState<ParkingConfig | null>(null)
  const togglingIdsRef = useRef<Set<number>>(new Set())

  const handleCreateDone = (newConfig: ParkingConfig) => {
    setParkingConfigs([newConfig, ...parkingConfigs])
    setIsCreateDialogOpen(false)
  }

  const handleUpdateDone = (updatedConfig: ParkingConfig) => {
    setParkingConfigs(
      parkingConfigs.map(config => (config.id === updatedConfig.id ? updatedConfig : config)),
    )
    setIsUpdateDialogOpen(false)
    setSelectedConfig(null)
  }

  const handleDeleteDone = () => {
    if (!selectedConfig) return
    setParkingConfigs(parkingConfigs.filter(config => config.id !== selectedConfig.id))
    setIsDeleteDialogOpen(false)
    setSelectedConfig(null)
  }

  const handleToggleActive = async (config: ParkingConfig, nextActive: boolean) => {
    if (config.active === nextActive || togglingIdsRef.current.has(config.id)) return

    togglingIdsRef.current.add(config.id)
    setParkingConfigs(current =>
      current.map(item => (item.id === config.id ? { ...item, active: nextActive } : item)),
    )

    try {
      const { parkingConfig: updatedConfig } = await setActiveApi({
        id: config.id,
        active: nextActive,
      })
      setParkingConfigs(current =>
        current.map(item => (item.id === updatedConfig.id ? updatedConfig : item)),
      )
    } catch (err) {
      console.error(err)
      setParkingConfigs(current =>
        current.map(item => (item.id === config.id ? { ...item, active: config.active } : item)),
      )
      snackbar.show(t('parking:errors.toggleActiveFailed') as string)
    } finally {
      togglingIdsRef.current.delete(config.id)
    }
  }

  const columns = [
    {
      title: t('parking:columns.id') as string,
      field: 'id',
      cellStyle: column95Style,
      headerStyle: column95Style,
    },
    {
      title: t('parking:columns.benefit') as string,
      sorting: false,
      customFilterAndSearch: matchesBenefitSearch,
      render: (rowData: ParkingConfig) => {
        const title = getConfigTitle(rowData)
        const plainTitle = getPlainConfigTitle(rowData)
        const iconUrl = getConfigPrize(rowData)?.iconUrl
        return (
          <Box display="flex" alignItems="center" style={{ minWidth: 0, gap: 8 }}>
            {iconUrl && (
              <img
                src={iconUrl}
                alt=""
                style={{ width: 28, height: 28, objectFit: 'contain', flexShrink: 0 }}
              />
            )}
            <Tooltip title={plainTitle} placement="bottom-start">
              <span
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(title) }}
              />
            </Tooltip>
            {isOutOfValidity(rowData) && (
              <Chip
                size="small"
                label={t('parking:status.outOfValidity') as string}
                style={{
                  backgroundColor: COLORS.LIGHT_GREY,
                  color: COLORS.FONT_SECONDARY,
                  border: 'none',
                }}
              />
            )}
          </Box>
        )
      },
    },
    {
      title: t('parking:columns.levels') as string,
      sorting: false,
      cellStyle: column232Style,
      headerStyle: column232Style,
      render: (rowData: ParkingConfig) =>
        rowData.levels?.length
          ? rowData.levels.map(level => (
              <Box key={level.id} mr={1} mb={0.5} display="inline-block">
                <Chip
                  size="small"
                  label={level.membership?.title ?? `#${level.membershipId}`}
                  style={getMembershipChipStyle(level.membership?.level)}
                />
              </Box>
            ))
          : (t('parking:emptyValue') as string),
    },
    {
      title: t('parking:columns.type') as string,
      defaultSort: 'asc' as const,
      customSort: (a: ParkingConfig, b: ParkingConfig) =>
        getParkingKindRank(a) - getParkingKindRank(b),
      customFilterAndSearch: (term: string, config: ParkingConfig) =>
        removeAccents(getKindLabel(config, translate))
          .toLowerCase()
          .includes(removeAccents(term).toLowerCase()),
      cellStyle: column232Style,
      headerStyle: column232Style,
      render: (rowData: ParkingConfig) => getKindLabel(rowData, translate),
    },
    {
      title: t('parking:columns.mode') as string,
      customSort: (a: ParkingConfig, b: ParkingConfig) =>
        compareText(getModeLabel(a, translate), getModeLabel(b, translate)),
      cellStyle: column232Style,
      headerStyle: column232Style,
      render: (rowData: ParkingConfig) => getModeLabel(rowData, translate),
    },
    {
      title: t('parking:columns.active') as string,
      field: 'active',
      customSort: (a: ParkingConfig, b: ParkingConfig) => Number(a.active) - Number(b.active),
      cellStyle: column95Style,
      headerStyle: column95Style,
      render: (rowData: ParkingConfig) => {
        const tooltip = (rowData.active ? t('parking:deactivate') : t('parking:activate')) as string
        return (
          <Tooltip title={tooltip} placement="bottom">
            <span>
              <ToggleSwitch
                checked={Boolean(rowData.active)}
                onClick={event => event.stopPropagation()}
                onChange={(_event, checked) => handleToggleActive(rowData, checked)}
                inputProps={{ 'aria-label': tooltip }}
              />
            </span>
          </Tooltip>
        )
      },
    },
  ]

  const actions = [
    {
      icon: () => <ListOrdered size={20} />,
      tooltip: t('parking:order.action') as string,
      buttonText: t('parking:order.action') as string,
      isFreeAction: true,
      onClick: () => setIsOrderDialogOpen(true),
    },
    {
      icon: 'add',
      tooltip: t('parking:newConfig') as string,
      isFreeAction: true,
      onClick: () => setIsCreateDialogOpen(true),
    },
    {
      icon: () => <Pencil size={20} />,
      tooltip: t('parking:edit') as string,
      onClick: (_: React.SyntheticEvent, row: ParkingConfig | ParkingConfig[]) => {
        setSelectedConfig(row as ParkingConfig)
        setIsUpdateDialogOpen(true)
      },
    },
    {
      icon: 'delete',
      tooltip: t('parking:delete') as string,
      onClick: (_: React.SyntheticEvent, row: ParkingConfig | ParkingConfig[]) => {
        setSelectedConfig(row as ParkingConfig)
        setIsDeleteDialogOpen(true)
      },
    },
  ]

  return (
    <>
      <DefaultTable
        title={t('parking:listTitle') as string}
        columns={columns}
        data={parkingConfigs}
        isLoading={isLoading}
        actions={actions}
        options={{
          rowStyle: (data: ParkingConfig, index: number) => ({
            backgroundColor: index % 2 === 0 ? '#FAFAFA' : '#FFF',
            height: 56,
            opacity: data.active ? 1 : 0.55,
            boxShadow: `inset 4px 0 0 ${data.isFree ? COLORS.PRIMARY : 'transparent'}`,
          }),
        }}
      />

      {isCreateDialogOpen && (
        <CreateParkingConfigDialog
          handleClose={() => setIsCreateDialogOpen(false)}
          onDone={handleCreateDone}
        />
      )}

      {isOrderDialogOpen && (
        <OrderParkingBenefitsDialog handleClose={() => setIsOrderDialogOpen(false)} />
      )}

      {isUpdateDialogOpen && selectedConfig && (
        <UpdateParkingConfigDialog
          handleClose={() => {
            setIsUpdateDialogOpen(false)
            setSelectedConfig(null)
          }}
          parkingConfig={selectedConfig}
          onDone={handleUpdateDone}
        />
      )}

      {isDeleteDialogOpen && selectedConfig && (
        <DeleteParkingConfigDialog
          handleClose={() => {
            setIsDeleteDialogOpen(false)
            setSelectedConfig(null)
          }}
          parkingConfigId={selectedConfig.id}
          parkingConfigTitle={getPlainConfigTitle(selectedConfig)}
          onDone={handleDeleteDone}
        />
      )}
    </>
  )
}

export { ParkingConfigs }
